import { secondAxios } from "../../../../api/axios";
import { fetchAllRevsForProject, fetchPipingRev } from "./api";
import { extractPipingRevisionData } from "./pipingRevisions";

// Where a Piping Estimation WBS gets its specs from.
//
// Mirrors the Software (Piping/ParentTabsTest/specSourceLink.js): a plant's
// Spec is its own record, holding its own selectedSpecs / customSpecRows /
// specHelperConfigs, and every module that needs specs reads them from that
// record rather than keeping its own. A plant is limited to one Spec record
// (see SINGLETON_PROJECT_TYPES), so the lookup is by plant, not a picker.

const normalizeType = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** All projects in a plant, or [] if they can't be read. */
async function fetchPlantRows(plantId, clientName) {
  if (!plantId || !clientName) return [];

  const res = await secondAxios.get(`project/plant/${plantId}/${clientName}`);
  const body = res?.data;
  return Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
}

/** The saved payload of a project's latest revision, or null. */
async function readLatestRevisionData(projectId) {
  const revisions = await fetchAllRevsForProject(projectId);
  const list = Array.isArray(revisions) ? revisions : [];
  if (!list.length) return null;

  const latest = list[list.length - 1];
  return extractPipingRevisionData(await fetchPipingRev(projectId, latest.id));
}

/**
 * The plant's Spec record and the spec data saved on it.
 *
 * `data` is null when there is nothing to link - no Spec record, or several.
 * Several is only possible for a plant that predates the one-per-plant rule,
 * and is reported through `candidates` rather than guessed at: estimating
 * against the wrong spec is far costlier than a visibly missing one.
 */
export async function resolvePlantSpec(plantId, clientName) {
  const candidates = (await fetchPlantRows(plantId, clientName)).filter(
    (row) => normalizeType(row?.type) === "spec",
  );
  const source = candidates.length === 1 ? candidates[0] : null;
  if (!source) return { source, candidates, data: null };

  const saved = await readLatestRevisionData(source.id);
  if (!saved) return { source, candidates, data: null };

  return {
    source,
    candidates,
    data: {
      selectedSpecs: Array.isArray(saved.selectedSpecs)
        ? saved.selectedSpecs
        : [],
      customSpecRows: Array.isArray(saved.customSpecRows)
        ? saved.customSpecRows
        : [],
      specHelperConfigs:
        saved.specHelperConfigs && typeof saved.specHelperConfigs === "object"
          ? saved.specHelperConfigs
          : {},
    },
  };
}
